// Autocomplete de lugares (Google Places) com dropdown PRÓPRIO.
//
// Por que não usamos mais o widget legado `google.maps.places.Autocomplete`:
// ele tem um bug conhecido (e que o Google NÃO vai mais corrigir — virou
// "legacy"/deprecated em 2025) em que clicar numa sugestão às vezes devolve
// OUTRO lugar (o `place_changed`/`getPlace()` retorna a previsão errada
// quando a lista re-renderiza no instante do clique). Era exatamente o bug
// reportado: "seleciono uma sugestão e ele preenche outro endereço".
//
// Solução: nós mesmos buscamos as previsões via a Places API (New)
// `AutocompleteSuggestion.fetchAutocompleteSuggestions`, renderizamos o
// dropdown e amarramos CADA linha ao seu `placeId` exato. Ao clicar, buscamos
// os detalhes daquele placeId específico (`prediction.toPlace().fetchFields`).
// Não há ambiguidade: a linha clicada é o lugar retornado.
//
// Reutilizamos as classes `.pac-container` / `.pac-item` / `.pac-item-query`
// pra herdar o tema (claro/escuro) que o CSS do app já define pra elas.
//
// API (compatível com os callers existentes):
//   const detach = await attachPlacesAutocomplete(inputEl, (place) => {...}, opts?);
//   place = { name, lat, lng, placeId, country }
//   opts  = { clearAfter?: bool, cities?: bool, includeCountry?: bool }
//   detach() limpa listeners e remove o dropdown.

import { loadGoogleMaps, getGoogleMapsLibrary } from '../../features/map/places.js';
import { ensureKeyboardInsetTracking } from '../../core/keyboardInset.js';

// Suprime o popup nativo do Google quando a key tem restrição/problema.
if (typeof window !== 'undefined' && !window.gm_authFailure) {
  window.gm_authFailure = function () {
    console.warn('[places] Google Maps auth failure — autocomplete desabilitado');
  };
}

const DEBOUNCE_MS = 200;

export async function attachPlacesAutocomplete(input, onSelect, opts = {}) {
  if (!input) return () => {};

  // Rastreia a altura do teclado virtual (--app-kb-height) pros modais
  // subirem acima do teclado no celular — assim o dropdown não fica escondido.
  ensureKeyboardInsetTracking();

  const clearAfter = !!opts.clearAfter;
  const includeCountry = !!opts.includeCountry;
  const cities = !!opts.cities;

  let placesLib;
  try {
    await loadGoogleMaps();
    placesLib = await getGoogleMapsLibrary('places');
  } catch (err) {
    console.warn('[places] SDK falhou — input fica como texto puro:', err);
    return () => {};
  }

  const AutocompleteSuggestion = placesLib?.AutocompleteSuggestion
    || window.google?.maps?.places?.AutocompleteSuggestion;
  const AutocompleteSessionToken = placesLib?.AutocompleteSessionToken
    || window.google?.maps?.places?.AutocompleteSessionToken;

  // Sem a API New disponível, cai pro fallback legado (melhor que nada).
  if (!AutocompleteSuggestion || !AutocompleteSessionToken) {
    console.warn('[places] AutocompleteSuggestion (API New) indisponível — fallback legado');
    return attachLegacyFallback(input, onSelect, { clearAfter, includeCountry });
  }

  let sessionToken = new AutocompleteSessionToken();
  let predictions = [];      // [{ prediction, placeId, mainText, secondaryText }]
  let activeIndex = -1;
  let debounceTimer = null;
  let lastQuery = '';
  let destroyed = false;

  // ─── Dropdown próprio (reusa classes .pac-* pro tema do app) ──
  const dropdown = document.createElement('div');
  dropdown.className = 'pac-container pac-container--owned';
  dropdown.setAttribute('role', 'listbox');
  dropdown.style.position = 'fixed';
  dropdown.style.zIndex = '100000';
  dropdown.style.display = 'none';
  dropdown.style.overflowY = 'auto';
  dropdown.style.maxHeight = '46vh';
  document.body.appendChild(dropdown);

  function positionDropdown() {
    const r = input.getBoundingClientRect();
    dropdown.style.left = `${r.left}px`;
    dropdown.style.top = `${r.bottom}px`;
    dropdown.style.width = `${r.width}px`;
  }
  function showDropdown() { positionDropdown(); dropdown.style.display = 'block'; }
  function hideDropdown() { dropdown.style.display = 'none'; activeIndex = -1; }
  const isOpen = () => dropdown.style.display !== 'none';

  function renderDropdown() {
    if (predictions.length === 0) { hideDropdown(); return; }
    const rows = predictions.map((p, i) => {
      const sel = i === activeIndex ? ' pac-item-selected' : '';
      const secondary = p.secondaryText
        ? ` <span class="pac-item-secondary">${esc(p.secondaryText)}</span>` : '';
      return `<div class="pac-item${sel}" role="option" data-idx="${i}" aria-selected="${i === activeIndex}">`
        + `<span class="pac-item-query">${esc(p.mainText)}</span>${secondary}</div>`;
    }).join('');
    // Atribuição "Powered by Google" — exigida ao exibir previsões do Places
    // fora de um mapa Google (o widget legado mostrava automaticamente).
    const attribution = '<div class="pac-attribution" aria-hidden="true" '
      + 'style="display:flex;justify-content:flex-end;align-items:center;'
      + 'padding:4px 8px;opacity:.8;">'
      + '<img alt="Powered by Google" height="12" '
      + 'src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-non-white3.png"></div>';
    dropdown.innerHTML = rows + attribution;
    showDropdown();
  }

  async function fetchPredictions(q) {
    const baseReq = { input: q, sessionToken, language: 'pt-BR' };
    const req = cities ? { ...baseReq, includedPrimaryTypes: ['(cities)'] } : baseReq;
    let res = null;
    try {
      res = await AutocompleteSuggestion.fetchAutocompleteSuggestions(req);
    } catch (err) {
      // Filtro de tipo pode ser rejeitado — tenta sem ele.
      if (cities) {
        try { res = await AutocompleteSuggestion.fetchAutocompleteSuggestions(baseReq); }
        catch { /* noop */ }
      }
      if (!res) { console.warn('[places] fetchAutocompleteSuggestions falhou:', err?.message || err); return; }
    }
    if (destroyed || input.value.trim() !== q) return;  // resposta obsoleta
    predictions = (res.suggestions || [])
      .map((s) => s.placePrediction)
      .filter(Boolean)
      .map((pp) => ({
        prediction: pp,
        placeId: pp.placeId,
        // `text` = descrição completa da previsão ("Camp Nou, Barcelona,
        // Espanha") — é o que o usuário lê e clica. Usamos isso pra preencher
        // o campo (NÃO o formattedAddress, que pra estabelecimentos vira só a
        // rua/bairro e parece "outro endereço").
        text: pp.text?.text || '',
        mainText: pp.mainText?.text || pp.text?.text || '',
        secondaryText: pp.secondaryText?.text || '',
      }));
    activeIndex = -1;
    renderDropdown();
  }

  async function choose(idx) {
    const entry = predictions[idx];
    if (!entry?.prediction) return;
    hideDropdown();
    try {
      const place = entry.prediction.toPlace();
      const fields = ['location', 'formattedAddress', 'displayName', 'id'];
      if (includeCountry) fields.push('addressComponents');
      await place.fetchFields({ fields });
      const loc = place.location;
      const lat = loc ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null;
      const lng = loc ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null;
      const displayName = typeof place.displayName === 'string'
        ? place.displayName : (place.displayName?.text || '');
      const address = place.formattedAddress || '';
      // Rótulo visível = a descrição que o usuário clicou (nome + região),
      // com fallbacks. NÃO usamos `formattedAddress` como rótulo principal —
      // pra estabelecimentos ele perde o nome (ex: "Camp Nou" virava
      // "Les Corts, 08028 Barcelona"). O endereço completo segue em `address`.
      const label = entry.text
        || [entry.mainText, entry.secondaryText].filter(Boolean).join(', ')
        || displayName
        || address
        || input.value;
      let country = '';
      if (includeCountry) {
        const comps = place.addressComponents || [];
        const c = comps.find((x) => (x.types || []).includes('country'));
        country = c?.longText || c?.long_name || '';
      }
      input.value = clearAfter ? '' : label;
      onSelect?.({
        name: label,
        address,
        displayName,
        lat,
        lng,
        placeId: place.id || entry.placeId || '',
        country,
      });
      // Novo token de sessão por seleção (boa prática de billing do Places).
      sessionToken = new AutocompleteSessionToken();
    } catch (err) {
      console.warn('[places] detalhes do lugar falharam:', err?.message || err);
    }
  }

  // ─── Listeners ───────────────────────────────────────────────
  function onInput() {
    const q = input.value.trim();
    lastQuery = q;
    clearTimeout(debounceTimer);
    if (!q) { predictions = []; hideDropdown(); return; }
    debounceTimer = setTimeout(() => {
      if (input.value.trim() === lastQuery) fetchPredictions(q);
    }, DEBOUNCE_MS);
  }

  function onKeyDown(e) {
    if (!isOpen() || predictions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIndex = Math.min(activeIndex + 1, predictions.length - 1);
      renderDropdown();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex = Math.max(activeIndex - 1, 0);
      renderDropdown();
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0) { e.preventDefault(); choose(activeIndex); }
    } else if (e.key === 'Escape') {
      hideDropdown();
    }
  }

  // mousedown (não click) + preventDefault: seleciona sem tirar o foco do
  // input antes do clique resolver (evita o blur fechar o dropdown antes).
  function onDropdownMouseDown(e) {
    const item = e.target.closest('.pac-item');
    if (!item) return;
    e.preventDefault();
    const idx = Number(item.dataset.idx);
    if (Number.isInteger(idx)) choose(idx);
  }
  function onDropdownMouseOver(e) {
    const item = e.target.closest('.pac-item');
    if (!item) return;
    const idx = Number(item.dataset.idx);
    if (Number.isInteger(idx) && idx !== activeIndex) { activeIndex = idx; renderDropdown(); }
  }

  function onBlur() { setTimeout(() => { if (!destroyed) hideDropdown(); }, 150); }
  function onReposition() { if (isOpen()) positionDropdown(); }

  input.addEventListener('input', onInput);
  input.addEventListener('keydown', onKeyDown);
  input.addEventListener('blur', onBlur);
  dropdown.addEventListener('mousedown', onDropdownMouseDown);
  dropdown.addEventListener('mouseover', onDropdownMouseOver);
  window.addEventListener('scroll', onReposition, true);
  window.addEventListener('resize', onReposition);

  // ─── Cleanup ─────────────────────────────────────────────────
  function cleanup() {
    if (destroyed) return;
    destroyed = true;
    clearTimeout(debounceTimer);
    input.removeEventListener('input', onInput);
    input.removeEventListener('keydown', onKeyDown);
    input.removeEventListener('blur', onBlur);
    window.removeEventListener('scroll', onReposition, true);
    window.removeEventListener('resize', onReposition);
    try { removalObserver.disconnect(); } catch { /* noop */ }
    try { dropdown.remove(); } catch { /* noop */ }
  }

  // Auto-cleanup: alguns callers (routeModal, hotelModal) não guardam o
  // detach. Quando o input sai do DOM (modal fechou/re-renderizou), nos
  // desmontamos sozinhos — senão os listeners de window (scroll/resize) e o
  // dropdown vazariam.
  const removalObserver = new MutationObserver(() => {
    if (!input.isConnected) cleanup();
  });
  removalObserver.observe(document.body, { childList: true, subtree: true });

  return cleanup;
}

// ─── Fallback: widget legado (só se a API New não existir) ──────
// Mantido por segurança. Tem o bug de seleção do Google, mas é melhor que
// deixar o input sem nenhum autocomplete em ambientes sem a API New.
function attachLegacyFallback(input, onSelect, { clearAfter, includeCountry }) {
  const g = window.google;
  if (!g?.maps?.places?.Autocomplete) return () => {};
  const ac = new g.maps.places.Autocomplete(input, {
    fields: ['name', 'formatted_address', 'geometry', 'place_id', 'address_components'],
    language: 'pt-BR',
  });
  const listener = ac.addListener('place_changed', () => {
    const p = ac.getPlace();
    if (!p?.geometry?.location) return;
    let country = '';
    if (includeCountry) {
      country = (p.address_components || [])
        .find((c) => (c.types || []).includes('country'))?.long_name || '';
    }
    // Nome do lugar primeiro (ex: "Camp Nou"); formatted_address fica em
    // `address`. Sem isso o campo mostrava só a rua/bairro.
    const label = p.name || p.formatted_address || input.value;
    if (clearAfter) input.value = ''; else input.value = label;
    onSelect?.({
      name: label,
      address: p.formatted_address || '',
      displayName: p.name || '',
      lat: p.geometry.location.lat(),
      lng: p.geometry.location.lng(),
      placeId: p.place_id || '',
      country,
    });
  });
  return () => {
    if (listener?.remove) listener.remove();
    if (g.maps.event?.clearInstanceListeners) g.maps.event.clearInstanceListeners(ac);
    document.querySelectorAll('.pac-container').forEach((el) => {
      // remove dropdowns órfãos do widget legado
      if (!el.classList.contains('pac-container--owned')) {
        try { el.remove(); } catch { /* noop */ }
      }
    });
  };
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
